<template>
    <div id="workflow-run-setup-area" class="h-100">

        <div class="row h-100">
            
            <!-- TOP BAR -->
            <!-- <div class="col-12">  
            </div> -->

            <div class="col-auto border-right pt-4">  <!-- LEFT SIDEBAR -->
                <h3 class="mb-3">Setup</h3>
                <b-nav vertical class="spl-step-nav">
                    <b-nav-item
                        v-for="(tab, idx) in tabs"
                        :key="tab.ref"
                        :disabled="tab.disabled"
                        :active="currentTabObj.ref === tab.ref"
                        link-classes="d-flex align-items-center"
                        @click="goToTab(tab.ref, true)"
                    >
                        <!-- <number-round :text="tab.showErrors ? '' : (idx + 1)" :icon="tab.showErrors ? 'x' : ''" :variant="tab.showErrors ? 'danger-200' : ''" class="mr-2"></number-round> -->
                        <number-round :text="(idx + 1)" class="mr-2"></number-round>
                        <div :class="tab.showErrors ? 'text-danger' : ''">{{ tab.buttonText }}</div>
                    </b-nav-item>
                </b-nav>
            </div>
        
            <div id="workflow-run-setup-main-col" class="col pt-4">  <!-- MAIN AREA -->

                <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
                
                <!-- STEP TITLE AND BUTTONS -->
                <div 
                    class="w-100 d-flex justify-content-between align-items-center flex-wrap rfx-wizard-top-bar"
                >
                    <div class="w-50 d-flex align-items-center my-2">
                        <number-round :text="(currentTabIndex + 1)" variant="primary" class="mr-3"></number-round>
                        <div>
                            <h4 class=""> 
                                {{ currentTabObj.title }}
                            </h4>
                            <p class="mb-0">{{ currentTabObj.subtitle }}</p>
                        </div>
                    </div>
                    
                    <div class="d-flex justify-content-center border rounded-pill shadow overflow-hidden my-2 ml-auto">
                        <div v-if="!currentTabObj.buttons.previous.hide" class="h-100" style="border-right: 1px solid var(--gray-300)">
                            <b-button 
                                :disabled="!hasPreviousTab || currentTabObj.buttons.previous.disabled" 
                                variant="white-secondary" 
                                size="lg" 
                                class="text-nowrap rounded-0"
                                @click="goPreviousTab"
                            >
                                <div class="d-flex align-items-center">
                                    <b-icon icon="chevron-double-left" class="mr-2"></b-icon>
                                    <div class="text-nowrap">
                                        Previous
                                    </div>
                                </div>
                            </b-button>
                        </div>
                        <div v-if="!currentTabObj.buttons.save.hide" class="h-100" style="border-right: 1px solid var(--gray-300)">
                            <b-button 
                                :disabled="currentTabObj.buttons.save.disabled" 
                                variant="white-primary" 
                                size="lg" 
                                class="text-nowrap rounded-0"
                                @click="saveCurrentTab"
                            >
                                Save
                            </b-button>
                        </div>
                        <div v-if="!currentTabObj.buttons.next.hide" class="h-100">
                            <b-button 
                                :disabled="!hasNextTab || currentTabObj.buttons.next.disabled" 
                                variant="white-success" 
                                size="lg" 
                                class="text-nowrap rounded-0"
                                @click="goNextTab"
                            >
                                <div class="d-flex align-items-center">
                                    <div class="text-nowrap">
                                        Next
                                    </div>
                                    <b-icon icon="chevron-double-right" class="ml-2"></b-icon>
                                </div>                    
                            </b-button>
                        </div>
                    </div>

                </div>

                <hr>

                <!-- STEP ERRORS -->
                <b-alert
                    :show="currentTabObj.showErrors"
                    variant="danger"
                >
                    <p class="mb-0 font-weight-bold">Fix the following errors:</p>
                    <p 
                        v-for="errorMsg in currentTabObj.errors"
                        :key="errorMsg"
                        class="mt-2 mb-0"
                    >{{ errorMsg }}</p>
                </b-alert>

                <!-- STEP TABS -->
                <b-tabs
                    v-model="currentTabIndex"
                    @input="handleTabsInput"
                    @activate-tab="handleActivateTab"
                    nav-wrapper-class="sr-only"
                >
                    
                    <b-tab
                        ref="tab1"
                        :active="'tab1' === currentTabObj.ref"
                    >
                        
                        <div class="row mb-4">
                            <div class="col">
                                <div class="d-flex align-items-end justify-content-between">
                                    <!-- <h5 class="text-dark mb-0">Basic Information</h5> -->
                                </div>
                                
                                <div class="border rounded-lg bg-gray-50 shadow-sm p-3">
                                    <div class="row mb-3">
                                        <div class="col-3">
                                            <h5>Title</h5>
                                            <p class="small text-muted">
                                                Enter a descriptive title for this issue.
                                            </p>
                                        </div>
                                        <div class="col-9">
                                            <div class="d-flex align-items-end">
                                                <hot-edit-input
                                                    label="Issue Title"
                                                    label-size="md"
                                                    v-model="instanceData.title"
                                                    size="lg"
                                                    :edit-mode="true"
                                                    label-vertical
                                                    :loading="loadingInstanceData || loadingTitle"
                                                    no-edit-class="font-size-120 font-weight-500"
                                                    @blur="updateTitle"
                                                    class="flex-fill"
                                                ></hot-edit-input>
                                                <div class="width-100 p-2">
                                                    <span v-if="titleUpdatedShowSuccess" class="font-weight-bold text-success">SAVED</span>
                                                </div>
                                            </div>
                                            
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row mb-4">
                            <div class="col">
                                <div class="border rounded-lg bg-gray-50 shadow-sm p-3">
                                    <div class="row mb-3">
                                        <div class="col-3">
                                            <h5>Internal Team</h5>
                                            <p class="small text-muted">
                                                Define the internal team who will work on this issue within the system.
                                            </p>
                                        </div>
                                        <div class="col-9">
                                            <div class="d-flex gap-5">
                                                <div class="min-width-250 max-width-300">
                                                    <div class="hot-edit-label mb-2">Internal Owner</div>
                                                    <Dropdown 
                                                        v-if="instanceEditMode"
                                                        key="ownerEditMode"
                                                        v-model="instanceData.owner"
                                                        :options="ownerOptions" 
                                                        optionValue="id"
                                                        optionLabel="full_name" 
                                                        dataKey="id" 
                                                        :filter="true" 
                                                        placeholder="Select owner..." 
                                                        :showClear="true"
                                                        class="w-100"
                                                        :disabled="loadingInstanceData || tuIdsWithAssignedTasks.includes(selectedOwnerObj.id)"
                                                    >  <!-- :disabled="loadingInstanceData" -->
                                                    <!-- must add || !isDraftState to :disabled above! removed for testing  -->
                                                    <!-- works, but makes things a bit trickier and maybe not needed  @before-show="getAvailableVendors" -->
                                                        <template #indicator>
                                                            <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>  <!-- had to add this slot, even though it has default -->
                                                        </template>
                                                        <template #value="slotProps">
                                                            <div class="owner-options-item owner-options-item-value" v-if="ownerOptions.find(opt => opt.id === slotProps.value)">
                                                                <div>{{ ownerOptions.find(opt => opt.id === slotProps.value)['full_name'] }}</div>
                                                            </div>
                                                            <span v-else>
                                                                {{slotProps.placeholder}}
                                                            </span>
                                                        </template>
                                                        <template #option="slotProps">
                                                            <div v-if="loadingInstanceData" key="loadingOwnerOptions">
                                                                Loading...
                                                            </div>
                                                            <div v-else key="ownerOptionsLoaded" class="owner-options-item">
                                                                <div>{{slotProps.option.full_name}}</div>
                                                            </div>
                                                        </template>
                                                    </Dropdown>
                                                    <b-skeleton v-else-if="loadingInstanceData" key="ownerLoading" width="80%"></b-skeleton>
                                                    <div v-else key="ownerNotEditMode" class="font-size-100" :class="selectedOwnerObj ? 'font-weight-500' : 'text-muted font-weight-300'">
                                                        {{ selectedOwnerObj ? selectedOwnerObj.full_name : 'No Owner Selected' }}
                                                    </div>
                                                </div>
                                                <div class="flex-grow-1">
                                                    <div class="hot-edit-label mb-1">Internal Team Members</div>
                                                    <div class="text-muted small mb-3">  <!-- DESCRIPTION / HELP TEXT -->
                                                        These are the people from your company who have access to this issue.
                                                    </div>
                                                    <div v-if="instanceEditMode" key="intMemEdit" class="">
                                                        <b-form-group
                                                            v-if="internalMemberOptions.length"
                                                            key="hasInternalMemberOptions"
                                                            v-slot="{ ariaDescribedby }"
                                                        >
                                                            <b-form-checkbox
                                                                v-for="memberObj in internalMemberOptions"
                                                                :checked="checkedInternalMemberIds"
                                                                @input="handleInternalMemberChecked"
                                                                :disabled="tuIdsWithAssignedTasks.includes(memberObj.id)"
                                                                :key="memberObj.id"
                                                                :value="memberObj.id"
                                                                :aria-describedby="ariaDescribedby"
                                                                inline
                                                            >
                                                                {{ memberObj.full_name }}
                                                            </b-form-checkbox>
                                                        </b-form-group>
                                                        <div v-else key="noInternalMemberOptions" class="text-muted mb-2">
                                                            No available team members
                                                        </div>
                                                    </div>
                                                    <div v-else-if="loadingInstanceData" key="intMemLoading" class="text-muted">
                                                        Loading...
                                                    </div>
                                                    <div v-else-if="!instanceEditMode" key="intMemNoEdit" class="">
                                                        <div class="d-flex flex-wrap gap-2">
                                                            <tenant-user-badge
                                                                v-for="tu in selectedInternalMembersForDisplay"
                                                                :key="tu.id"
                                                                :tu-data="tu"
                                                            ></tenant-user-badge>
                                                        </div>
                                                    </div>
                                                    <div v-if="instanceEditMode && checkedInternalMemberIds.some(tuId => tuIdsWithAssignedTasks.includes(tuId))" class="mt-2 text-muted small">
                                                        <span class="font-weight-bold">NOTE:</span>&nbsp;
                                                        Existing members cannot be removed unless they are removed from all task assignments first.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr v-if="!isInternalOnly">
                                    <div v-if="!isInternalOnly" class="row mb-3">
                                        <div class="col-3">
                                            <h5>Vendor</h5>
                                            <p class="small text-muted">
                                                Select the related vendor and responsible individuals from that vendor.
                                            </p>
                                        </div>
                                        <div class="col-9">
                                            <div class="d-flex gap-5">
                                                <div class="min-width-250 max-width-300">
                                                    <div class="hot-edit-label mb-2">Vendor</div>
                                                    <Dropdown 
                                                        v-if="instanceEditMode"
                                                        key="vendorEditMode"
                                                        v-model="instanceData.vendor" 
                                                        @input="handleVendorInput"
                                                        :options="vendorOptions" 
                                                        optionLabel="name" 
                                                        optionValue="id"
                                                        dataKey="id" 
                                                        :filter="true" 
                                                        placeholder="Select vendor..." 
                                                        :showClear="true"
                                                        class="w-100"
                                                        :disabled="loadingInstanceData || !isDraftState"
                                                    >  <!-- must add || !isDraftState to :disabled above! removed for testing  -->
                                                    <!-- works, but makes things a bit trickier and maybe not needed  @before-show="getAvailableVendors" -->
                                                        <template #indicator>
                                                            <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>  <!-- had to add this slot, even though it has default -->
                                                        </template>
                                                        <template #value="slotProps">
                                                            <div class="supplier-options-item supplier-options-item-value" v-if="vendorOptions.find(opt => opt.id === slotProps.value)">
                                                                <div>{{ vendorOptions.find(opt => opt.id === slotProps.value)['name'] }}</div>
                                                            </div>
                                                            <span v-else>
                                                                {{slotProps.placeholder}}
                                                            </span>
                                                        </template>
                                                        <template #option="slotProps">
                                                            <div v-if="loadingInstanceData" key="loadingVendorList">
                                                                Loading...
                                                            </div>
                                                            <div v-else key="vendorListLoaded" class="supplier-options-item">
                                                                <div>{{slotProps.option.name}}</div>
                                                            </div>
                                                        </template>
                                                    </Dropdown>
                                                    <b-skeleton v-else-if="loadingInstanceData" key="vendorLoading" width="80%"></b-skeleton>
                                                    <div v-else key="vendorNotEditMode" class="font-size-100" :class="selectedVendorName ? 'font-weight-500' : 'text-muted font-weight-300'">
                                                        {{ selectedVendorName || 'No Vendor Selected' }}
                                                    </div>
                                                </div>
                                                <div class="flex-grow-1">
                                                    <!-- add a :key by vendor ID so when you change vendor it destroys/recreates  -->
                                                    <div class="hot-edit-label mb-1">Vendor Team Members</div>
                                                    <div class="text-muted small mb-3">  <!-- DESCRIPTION / HELP TEXT -->
                                                        Supplier contacts who will participate in this, and can be assigned to specific tasks in the workflow.
                                                    </div>
                                                    <div 
                                                        v-if="instanceEditMode && hasVendorSelected && !loadingVendorMemberOptions" 
                                                        :key="instanceData.vendor || 'no-vendor'" 
                                                        class=""
                                                    >
                                                        <b-form-group
                                                            v-if="vendorMemberOptions.length"
                                                            v-slot="{ ariaDescribedby }"
                                                        >
                                                            <b-form-checkbox
                                                                v-for="memberObj in vendorMemberOptions"
                                                                v-model="instanceData.vendor_members"
                                                                :disabled="tuIdsWithAssignedTasks.includes(memberObj.id)"
                                                                :key="memberObj.id"
                                                                :value="memberObj.id"
                                                                :aria-describedby="ariaDescribedby"
                                                                inline
                                                            >
                                                                {{ memberObj.full_name }}
                                                            </b-form-checkbox>
                                                        </b-form-group>
                                                        <div v-else key="noVendorMemberOptions" class="text-muted mb-2">
                                                            No existing contacts at this vendor
                                                        </div>
                                                        <!-- ADD NEW CONTACT - keep inside of element that has key to change when selected vendor changes -->
                                                        <div v-if="instanceData.vendor">
                                                            <b-button size="sm" variant="minimal-primary" @click="$refs.createContactModal.startCreate()">
                                                                <b-icon icon="plus-circle" class="mr-1"></b-icon>
                                                                Add New
                                                            </b-button>
                                                            <contact-create-update-modal
                                                                ref="createContactModal"
                                                                :new-data="{company: instanceData.vendor}"
                                                                :modal-title="`Add new contact at ${vendorOptions.find(v => v.value === instanceData.vendor)['text'] || 'this vendor'}`"
                                                                hide-company-input
                                                                save-close-html="Save"
                                                                hide-save-redirect
                                                                email-required
                                                                @create-complete="handleNewVendorContact"
                                                            ></contact-create-update-modal>
                                                        </div>
                                                    </div>
                                                    <div v-else-if="loadingInstanceData" key="venMemLoading" class="text-muted">
                                                        Loading...
                                                    </div>
                                                    <div v-else-if="!hasVendorSelected" key="venMemLoading" class="text-muted">
                                                        Select a vendor first
                                                    </div>
                                                    <div v-else-if="!instanceEditMode" key="venMemNoEdit" class="">
                                                        <div class="d-flex flex-wrap gap-2">
                                                            <tenant-user-badge
                                                                v-for="tu in selectedVendorMemberObjects"
                                                                :key="tu.id"
                                                                :tu-data="tu"
                                                            ></tenant-user-badge>
                                                        </div>
                                                    </div>
                                                    <div v-if="instanceEditMode && instanceData.vendor_members.some(vmId => tuIdsWithAssignedTasks.includes(vmId))" class="mt-2 text-muted small">
                                                        <span class="font-weight-bold">NOTE:</span>&nbsp;
                                                        Existing members cannot be removed unless they are removed from all task assignments first.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!instanceEditMode" key="notEditMode" class="d-flex justify-content-end gap-3">
                                        <b-button @click="enterEditMode" variant="secondary">  <!-- :disabled="!isDraftState" -->
                                            Edit
                                        </b-button>
                                    </div>
                                    <div v-if="instanceEditMode" key="isEditMode" class="d-flex justify-content-end gap-3">
                                        <b-button @click="cancelEditMode" variant="minimal-secondary" :disabled="savingUpdate">
                                            Cancel
                                        </b-button>
                                        <b-button @click="updateInstanceData" variant="primary" :disabled="savingUpdate || !hasUnsavedInstanceData">
                                            Save Changes
                                        </b-button>
                                    </div>
                                    <!-- <pre>{{ instanceData }}</pre> -->
                                </div>
                            </div>
                        </div>

                    </b-tab>

                    <b-tab
                        ref="tab2"
                        :active="'tab2' === currentTabObj.ref"
                    >
                        
                        <div class="row">
                            <div class="col" ref="jspCont">
                                <div class="p-3 mb-5">
                                    <div class="d-flex align-items-end justify-content-between mb-3">
                                        <div>
                                            <h5>Assign Tasks</h5>
                                            <p class="small text-muted mb-0">
                                                Configure the process flow for this specific issue, including who is assigned to each task, approvals, etc.
                                            </p>
                                        </div>
                                        <div class="">
                                            
                                        </div>
                                    </div>

                                    <transition name="fade-fast" mode="out-in">
                                    <div v-if="loading || loadingUpdateOptions" class="w-100 min-height-200 d-flex align-items-center justify-content-center">
                                        <busy-spinner></busy-spinner>
                                    </div>

                                    <div v-else class="bg-gray-100 border overflow-hidden shadow-sm rounded-lg">
                                        <div
                                            v-for="(step, stepIndex) in stepsWithTasks"
                                            :key="step.id"
                                            class=""
                                        >
                                            <!-- <div class="w-100 d-flex align-items-center justify-content-between">

                                                <div class="px-3 py-1 d-flex justify-content-start align-items-center">
                                                    <div 
                                                        :key="`${step.id}-icon`"
                                                        class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                                                        :class="`bg-${step.color_variant || 'primary'}-100 text-${step.color_variant || 'primary'}-600`" 
                                                        style="height: 2rem; width: 2rem;"
                                                    >
                                                        {{ stepIndex + 1 }}
                                                    </div>
                                                    <div class="ml-3 text-left">
                                                        <div class="text-dark font-weight-bold font-size-100">{{ step.title }}</div>
                                                        <p class="small text-muted mb-0">{{ step.description }}</p>
                                                    </div>
                                                </div>

                                                <div class="px-3 py-1 min-width-200 max-width-200 d-flex justify-content-end align-items-center">
                                                    <span v-if="step.isError" class="text-danger text-uppercase font-weight-500 font-size-80 mr-2">Needs Attention</span>
                                                    <b-icon :icon="step.isError ? 'exclamation-circle' : 'check-circle'" :variant="step.isError ? 'danger' : 'success'"></b-icon>
                                                </div>

                                            </div> -->

                                            <div class="d-flex align-items-start">

                                                <div class="py-4 min-width-200 max-width-200 px-3">
                                                    <div class="d-flex justify-content-start align-items-start">
                                                        <div class="d-flex flex-column align-items-center">
                                                            <div class="text-center text-primary text-uppercase font-size-80 font-weight-bold">Step</div>
                                                            <div 
                                                                :key="`${step.id}-icon`"
                                                                class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                                                                :class="`bg-${step.color_variant || 'primary'}-100 text-${step.color_variant || 'primary'}-600`" 
                                                                style="height: 2rem; width: 2rem; min-width: 2rem;"
                                                            >
                                                                {{ stepIndex + 1 }}
                                                            </div>
                                                        </div>
                                                        <div class="ml-3 text-left">
                                                            <div class="text-dark font-weight-bold font-size-100">{{ step.title }}</div>
                                                            <p class="small text-muted mb-0">{{ step.description }}</p>
                                                            <!-- <div class="mt-2 w-100 d-flex align-items-center justify-content-start">
                                                                <b-icon :icon="step.isError ? 'exclamation-circle-fill' : 'check-circle-fill'" :variant="step.isError ? 'danger' : 'success'"></b-icon>
                                                                <span class="text-uppercase font-weight-700 font-size-80 ml-2" :class="step.isError ? 'text-danger' : 'text-success'">{{ step.isError ? 'Incomplete' : 'Ready'}}</span>
                                                            </div> -->  <!-- you do not need it here AND on tasks AND overall -->
                                                        </div>
                                                    </div>
                                                    
                                                </div>

                                                <div class="py-4 flex-fill position-relative">
                                                    <div
                                                        :id="`step-${step.id}`"  
                                                        class="d-flex flex-wrap gap-5 justify-content-around"
                                                    >
                                                        <div v-if="(step.tasks.length < 1)" key="stepNoTasks" class="d-flex align-items-center justify-content-center">
                                                            <div>
                                                                {{ step.is_final_state ? 
                                                                'This is a final step - no tasks' : 
                                                                'There are no tasks for this step.  You can manually move the workflow from this step when ready.'}}
                                                            </div>
                                                        </div>
                                                        <workflow-task-setup
                                                            v-for="task in step.tasks"
                                                            :key="task.id"
                                                            :id="'task-' + task.id"
                                                            ref="taskNodes"
                                                            :task="task"
                                                            :read-only="false"
                                                            @updated="handleTaskUpdated"
                                                            class="max-width-350 min-width-350"
                                                        ></workflow-task-setup>
                                                    </div>
                                                    <div v-if="stepIndex < (stepsWithTasks.length - 1)" class="position-absolute" style="bottom: -21px; left: 50%; transform: translateX(-50%);">
                                                        <b-icon icon="arrow-down-short" font-scale="3"></b-icon>
                                                    </div>  <!-- chevron-compact-down -->
                                                </div>
                                            </div>

                                            <hr v-if="stepIndex < (stepsWithTasks.length - 1)" class="my-0">

                                            <!-- <div class="px-4 py-3 d-flex justify-content-between align-items-center">
                                                <div class="w-40 d-flex justify-content-start align-items-center">
                                                    <div 
                                                        :key="`${step.id}-icon`"
                                                        class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                                                        :class="`bg-${step.color_variant || 'primary'}-100 text-${step.color_variant || 'primary'}-600`" 
                                                        style="height: 2rem; width: 2rem;"
                                                    >
                                                        {{ stepIndex + 1 }}
                                                    </div>
                                                    <div class="ml-3 text-left">
                                                        <div class="text-dark font-weight-bold font-size-100">{{ step.title }}</div>
                                                        <p class="small text-muted mb-0">{{ step.description }}</p>
                                                    </div>
                                                </div>
                                                <div class="w-30 text-left">
                                                </div>
                                                <div class="w-30">
                                                    <div class="d-flex align-items-center justify-content-end">
                                                        <span v-if="step.isError" class="text-danger text-uppercase font-weight-500 font-size-80 mr-2">Needs Attention</span>
                                                        <b-icon :icon="step.isError ? 'exclamation-circle' : 'check-circle'" :variant="step.isError ? 'danger' : 'success'"></b-icon>
                                                    </div>
                                                </div>
                                            </div> -->
                                            <!-- <div
                                                :id="`step-${step.id}`"  
                                                class="p-3 d-flex flex-wrap gap-5 justify-content-around"
                                            >
                                                    <div v-if="(step.tasks.length < 1)" key="stepNoTasks" class="d-flex align-items-center justify-content-center">
                                                        <div>
                                                            {{ step.is_final_state ? 
                                                            'This is a final step - no tasks' : 
                                                            'There are no tasks for this step.  You can manually move the workflow from this step when ready.'}}
                                                        </div>
                                                    </div>
                                                    <workflow-task-setup
                                                        v-for="task in step.tasks"
                                                        :key="task.id"
                                                        :id="'task-' + task.id"
                                                        ref="taskNodes"
                                                        :task="task"
                                                        :read-only="false"
                                                        @updated="handleTaskUpdated"
                                                        class="max-width-350 min-width-350"
                                                    ></workflow-task-setup>
                                            </div> -->
                                        </div>
                                    </div>

                                    <!-- <div v-else class="accordion" role="tablist">
                                        <div
                                            v-for="(step, stepIndex) in stepsWithTasks"
                                            :key="step.id"
                                            class="bg-gray-100 mb-3 rounded-lg border"
                                        >
                                            <div class="" role="tab">
                                                <b-button 
                                                    block 
                                                    size="lg"
                                                    v-b-toggle="`step-${step.id}`" 
                                                    variant="white-secondary"
                                                    class="d-flex justify-content-between align-items-center"
                                                >
                                                    <div class="w-40 d-flex justify-content-start align-items-center">
                                                        <div 
                                                            :key="`${step.id}-icon`"
                                                            class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                                                            :class="`bg-${step.color_variant || 'primary'}-100 text-${step.color_variant || 'primary'}-600`" 
                                                            style="height: 2rem; width: 2rem;"
                                                        >
                                                            {{ stepIndex + 1 }}
                                                        </div>
                                                        <div class="ml-3 text-left">
                                                            <div class="text-dark">{{ step.title }}</div>
                                                            <p class="small text-muted mb-0">{{ step.description }}</p>
                                                        </div>
                                                    </div>
                                                    <div class="w-30 text-left">
                                                        <div 
                                                            v-if="step.tasks.length" 
                                                            class=" text-left"
                                                        >
                                                            {{ step.tasks.length }} Tasks
                                                        </div>
                                                    </div>
                                                    <div class="w-30">
                                                        <div class="d-flex align-items-center justify-content-end">
                                                            <span v-if="step.isError" class="text-danger text-uppercase font-weight-500 font-size-80 mr-2">Needs Attention</span>
                                                            <b-icon :icon="step.isError ? 'exclamation-circle' : 'check-circle'" :variant="step.isError ? 'danger' : 'success'"></b-icon>
                                                        </div>
                                                    </div>
                                                </b-button>
                                            </div>
                                            <b-collapse 
                                                :id="`step-${step.id}`"  
                                                visible
                                                accordion="workflow-steps"
                                                role="tabpanel"
                                                class=""
                                            >
                                                <div class="p-3 d-flex flex-wrap gap-5 justify-content-around">
                                                    <div v-if="(step.tasks.length < 1)" key="stepNoTasks" class="d-flex align-items-center justify-content-center">
                                                        <div>
                                                            {{ step.is_final_state ? 
                                                            'This is a final step - no tasks' : 
                                                            'There are no tasks for this step.  You can manually move the workflow from this step when ready.'}}
                                                        </div>
                                                    </div>
                                                    <workflow-task-setup
                                                        v-for="task in step.tasks"
                                                        :key="task.id"
                                                        :id="'task-' + task.id"
                                                        ref="taskNodes"
                                                        :task="task"
                                                        :read-only="false"
                                                        @updated="handleTaskUpdated"
                                                        class="max-width-350 min-width-350"
                                                    ></workflow-task-setup>
                                                </div>
                                            </b-collapse>
                                        </div>
                                    </div> -->
                                    </transition>
                                </div>
                            </div>
                        </div>

                    </b-tab>

                    <b-tab
                        ref="tab3"
                        :active="'tab3' === currentTabObj.ref"
                    >

                        <div class="mt-4 mb-3 min-height-300 d-flex justify-content-center align-items-center">
                            <div v-if="loading || loadingPossibleActions" key="checkingPossibleActions" class="d-flex flex-column align-items-center gap-4">
                                <busy-spinner text="Checking status..."></busy-spinner>
                            </div>
                            <div v-else-if="runData.is_workflow_started" key="actionsWorkflowStarted" class="d-flex flex-column align-items-center gap-4">
                                <div class="font-weight-bold text-muted">
                                    <span>Workflow has started</span> 
                                </div>
                                <b-button
                                    size="lg"
                                    variant="primary"
                                    :href="runData.urls.internal_detail"
                                >Go to Details &rarr;
                                </b-button>
                            </div>
                            <div v-else key="notStartedNotChecking" class="d-flex flex-column align-items-center gap-4">
                                <div class="font-weight-bold text-muted">
                                    <span v-if="!canProceed && isDraftState" key="mustFinishText">Must finish setup before starting workflow</span> 
                                    <span v-else-if="canProceed && isDraftState" key="readyText">Ready to start workflow</span> 
                                </div>
                                <b-button
                                    key="isDraftShowButton"
                                    size="lg"
                                    :variant="isDraftState ? 'success' : 'secondary'"
                                    :disabled="!canProceed || loadingAction"
                                    @click="callAction(startWorkflowActionName)"
                                >
                                    <span v-if="loadingAction" key="loadingActionSpan">
                                        <b-spinner small></b-spinner>
                                        <span class="ml-2">Launching...</span>
                                    </span>
                                    <span v-else key="notLoadingActionSpan">
                                        {{ isDraftState ? 'Start Workflow' : 'Workflow Started' }}
                                        <b-icon v-if="isDraftState" icon="play-fill"></b-icon>
                                    </span>
                                </b-button>
                            </div>
                        </div>

                    </b-tab>

                </b-tabs>

            </div>  <!-- END OF MAIN COLUMN -->
        </div>  <!-- END OF ROW -->

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import { WorkflowRun, DwfTask } from './workflow_models';
import Dropdown from 'primevue/dropdown';
//import BaseVendorSearchSelect from '../BaseVendorSearchSelect';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
//import MultiPageFormPage from '../formium/MultiPageFormPage.vue';
import BusySpinner from '../functional-comps/BusySpinner.vue';
//import StepSelectors from './StepSelectors.vue';
import NumberRound from '../functional-comps/NumberRound';
import WorkflowTaskSetup from './WorkflowTaskSetup.vue';

class TabObject {
    constructor({  // you can set defaults for constructor params here, or just let them be undefined and set in constructor method
        ref,  // REQUIRED
        title,  // REQUIRED
        subtitle,
        buttonText,
        // things set in checkTab:
        isValid = true,  // all validation OK for this tab?  set this in checkTab, if anywhere, start with true
        hasUnsaved = false,  // set this in checkTab, if anywhere
        disabled = false,
        errors,  // array of error strings
        wasVisited = false,
        showErrors = false,  // set to true when wasVisited = true and isValid = false 
        // saving:
        saveMethodName,  // string name of method to call to SAVE this tab
        buttons,  // should be an object, just pass in what is needed to override defaults, we merge them
    }) {
        // DEFAULTS
        const buttonDefaults = {
            save: { hide: false, disabled: false },
            previous: { hide: false, disabled: false },
            next: { hide: false, disabled: false },
        };
        this.ref = ref;
        this.title = title;
        this.subtitle = subtitle ?? '';
        this.buttonText = buttonText ?? this.title;
        this.isValid = isValid;
        this.hasUnsaved = hasUnsaved;
        this.disabled = disabled;
        this.errors = errors ?? [];
        this.wasVisited = wasVisited;
        this.showErrors = showErrors;
        this.saveMethodName = saveMethodName;
        this.buttons = merge({}, buttonDefaults, buttons);
    }
}

export default {
    name: 'WorkflowRunSetup',
    //store: store,
    components: {
        //BaseVendorSearchSelect,
        //MultiPageFormPage,
        BusySpinner,
        Dropdown,
        //StepSelectors,
        NumberRound,
        WorkflowTaskSetup,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        runId: {
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            loadingTitle: false,  // when doing updates only to title
            titleUpdatedShowSuccess: false,  // use setTimeout to show this temporarily
            loadingUpdateOptions: false,  
            loadingVendorMemberOptions: false,
            savingUpdate: false,
            runData: {},  // stash from API
            updateOptions: {},  // things like owner_options
            // PULL OUT KEY THINGS YOU WILL USE WITH V-MODEL TO TOP LEVEL?
            title: '',  // v-model
            // OWNER
            ownerOptions: [],  // from getUpdateOptions
            selectedOwnerObj: null,  // v-model with Dropdown, pull out ID when formatting for API
            selectedOwnerId: '',  
            // VENDOR
            vendorOptions: [],  // from getUpdateOptions
            selectedVendorObj: null,  // v-model with Dropdown, and set it when you get API data 
            // selectedVendorId - we'll pull it out when formatting for API -- since we use Dropdown component, the v-model is a full object
            // MEMBERS
            selectedInternalMemberObjects: [],  // array of Objects - current selections - full TU objects from API - update once you get response from API
            selectedVendorMemberObjects: [],  // array of Objects - current selections - full TU objects from API - update once you get response from API
            // LOCAL SELECTIONS - @input with checkboxes sets these - just the IDs of members / TUs
            selectedInternalMemberIds: [],  // then merge with owner in checkedInternalMemberIds
            selectedVendorMemberIds: [],
            // options from API
            internalMemberOptions: [],  // full TU objects -- OPTIONS not selections - but may not have all selections if a previously selected one is no longer an option
            vendorMemberOptions: [],  // full TU objects - OPTIONS not selections
            
            instanceFields: ['title', 'owner', 'vendor', 'vendor_members', 'internal_members'],
            instanceData: {},  // DATA FOR WorkflowRun - only native fields, no metadata - TODO - why do you have this, AND runData -- seems stupid
            lastSavedInstanceData: {},
            hasUnsavedInstanceData: false, // updated from checkHasUnsavedInstanceData
            instanceEditMode: false,
            possibleTransitions: [],  // ALWAYS USE FROM HERE -- will set it multiple ways - its like a store
            possibleActions: [],  // will be a list of strings... similar to possibleTransitions, but your homemade version - should not use possibleTransitions here
            startWorkflowActionName: 'start_workflow',
            loadingPossibleActions: false, // when refetching the list of actions
            loadingAction: false,
            // METADATA
            metadataSchema: [],  // must be mult-page capable
            metadata: {},  // split this out
            // SPEC
            workflow_spec: {},  
            // TASKS
            //tasks: [], // don't use this... just use tasksById 
            tasksById: {},  // map of tasks keyed by their ID -- our local 'store'
            // TABS 
            hideAllErrors: true,  // NEW Feb 17 2022 - set this to false (one-way) on initial data load... a way to prevent showing errors before initial data has even loaded - but must make sure to call checkAllTabs again after it changes... so wherever you set it false, make sure to call checkAllTabs AFTER that
            initialTabRef: 'tab1',
            currentTabIndex: 0,  // v-model with tabs - set it in created() based on initialTabRef
            previousVisitedTabRefs: null,  // a Set() - create it in created() when you also set initialTabRef and currentTabIndex - add tabRefs as you visit them, BEFORE you call checkTab()
            tabs: [],  // we set this in created() making TabObject class instances from tabsRaw
            tabsRaw: [
                {  // key is the ref name of the tab - or separate ref?
                    ref: 'tab1',
                    title: 'Basic Information',
                    subtitle: 'Basic information, including the involved supplier and contact people.',
                    buttonText: 'Basic Information',
                    saveMethodName: '',
                    buttons: {save: { hide: true } },
                },
                {
                    ref: 'tab2',
                    title: 'Process Flow',
                    subtitle: 'Assign people to each task/approval, set deadlines, etc.',
                    buttonText: 'Process Flow',
                    saveMethodName: '',
                    buttons: {save: { hide: true } },
                },
                {
                    ref: 'tab3',
                    title: 'Start',
                    subtitle: 'Start the workflow',
                    buttonText: 'Start',
                    saveMethodName: '',
                    buttons: {save: { hide: true } },
                },
            ],
        }
    },
    watch: {
        instanceData: {
            deep: true,
            handler: function() {
                // check the current tab when we update data
                // can you change this to know what things are on which tab?
                this.checkCurrentTab();  // update the current tab validation stuff
            }
        }
    },
    computed: {
        // TAB THINGS
        currentTabObj() {
            // from the key in tabs object -- should ALWAYS return one here -- template things expect it
            return this.tabs[this.currentTabIndex];
        },
        hasNextTab() {  // update this to allow for hidden / invisible tabs?
            return this.currentTabIndex < (this.tabs.length - 1);
        },
        hasPreviousTab() {  // update this to allow for hidden / invisible tabs?
            return this.currentTabIndex > 0;
        },
        nextTabObj() {
            // return full next tab object, or undefined? if no next tab
            return ( (this.currentTabIndex + 1) < this.tabs.length ) ? this.tabs[this.currentTabIndex + 1] : undefined;
        },
        previousTabObj() {
            // return full previous tab object, or undefined? if no previous tab
            return ( this.currentTabIndex > 0 ) ? this.tabs[this.currentTabIndex - 1] : undefined;
        },
         ////////
        loadingAny() {
            return this.loading || this.loadingVendorOptions || this.loadingUpdateOptions || this.loadingVendorMemberOptions;
        },
        hasVendorSelected() {
            return this.instanceData?.vendor;
        },
        selectedVendorName() {
            return this.selectedVendorObj?.name;
        },
        loadingInstanceData() {
            // loading the data or options (except for vendor members - handle separate)
            return this.loading || this.loadingUpdateOptions;
        },
        stepsWithTasks() {  // filter any hidden steps, then merge in the Tasks (linked through TaskSpec)
            // we want the actual tasks in there, not really the task specs -- the tasks themselves are what's being managed
            const allSteps = this.workflow_spec.steps ?? [];
            const stepsToShow = allSteps.filter(step => !step.is_hidden);
            const stepsWithTasks = stepsToShow.map(step => {
                const foundTasks = Object.values(this.tasksById).filter(t => step.id === t.step_id);
                // now we'll get an array of the template_task_id for any... which means they were created by a template task... will use this to hide template task if their concrete task is showing
                const templateTaskIds = foundTasks.map(task => task.template_task_id).filter(ttId => !!ttId);  // filter removes the nulls... probably many of them
                const tasksToShow = foundTasks.filter(task => (!templateTaskIds.includes(task.id) || task.needs_attention_setup));  // remove any template tasks if their concrete tasks were showing, but still show them if they needs_attention_setup for some reason
                const needsAttention = foundTasks.some(t => (t.needs_attention_setup));  // needs_attention_setup is calculated on backend
                return {...step, tasks: tasksToShow, isError: needsAttention};
            });
            // NEW FEB 19 2022 - filter out any steps with NO tasks
            // this will also hide things like 'closed' but that's OK... probably better that way!
            const stepsNotEmpty = stepsWithTasks.filter(step => (step.tasks.length > 0));
            const withExtraStuff = stepsNotEmpty.map((st) => {
                return { ...st, icon: 'record-fill'};
            });
            return withExtraStuff;
        },
        checkedInternalMemberIds() {
            // used in :checked of the internal members checkboxes - so pulls from different places as objects array below
            // these selections may not be saved yet
            const ownerId = this.instanceData.owner || '';
            return uniq([...this.instanceData.internal_members, ownerId].filter(id => !!id));  // added filter to remove undefined ownerObj.id if its not set
        },
        selectedInternalMembersForDisplay() {
            // FOR DISPLAY - combines owner with internal members if not already there - don't need a matching one for vendor members, since we don't have to add in owner
            const internalMembersCopy = [ ...this.selectedInternalMemberObjects ];  // shallow copy, but since we're not mutating anything INSIDE of TU objects here, we should be OK
            const internalMemberIds = internalMembersCopy.map(tu => tu.id);
            const ownerObj = this.selectedOwnerObj;
            if (ownerObj && ownerObj.id && !internalMemberIds.includes(ownerObj.id)) {
                internalMembersCopy.unshift(ownerObj);
            }
            return internalMembersCopy;
        },
        tuIdsWithAssignedTasks() {
            // returns array of TU IDs that are assigned to any task
            return uniq(Object.values(this.tasksById).map(task => task.assigned_tus.map(tu => tu.id)).flat());
        },
        isDraftState() {
            // return boolean if we're in sm_state === 'draft' or not
            return this.runData.sm_state === 'draft';
        },
        canProceed() {
            //const possibleTriggers = this.possibleTransitions?.map(trans => trans.trigger);
            //return !!possibleTriggers && possibleTriggers.includes('go_next_state');
            return !!this.possibleActions && Array.isArray(this.possibleActions) && this.possibleActions.includes(this.startWorkflowActionName);
        },
        isInternalOnly() {
            // is this an INTERNAL ONLY workflow based on DemoWorkflowSpec.internal_only?
            // if so, hide vendor related things
            return this.runData?.is_internal_only ?? true;  // default to true until loaded
        }
    },
    created() {
        // MAKE TABS! then set initial one
        const tabs = this.tabsRaw.map(tr => new TabObject(tr));
        this.tabs = tabs;
        // set initial tab index (based on initialTabRef) and create Set() for tracking which tabs you've visited
        // this assumes all the tabs are already in data... if you get them from API, then you have to do it later
        const initialTabIndex = this.tabs.findIndex(t => t.ref === this.initialTabRef);
        if (initialTabIndex > -1) {
            this.currentTabIndex = initialTabIndex;
        }
        this.previousVisitedTabRefs = new Set();  // DO NOT add initialTabRef to this... otherwise errors will show on first load of a new thing
    },
    mounted: function() {        
        // NORMAL STUFF
        this.getDetails();
        this.getUpdateOptions();
        this.getVendorMemberOptions();
        //this.loadMetadata();  // not needed for now
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        // TAB CONTROLS / METHODS
        async saveTab(tabRef) {
            console.log('saveTab', tabRef);
            // TODO - implement this!
        },
        async saveCurrentTab() {
            const currentTabRef = this.currentTabObj.ref;
            return await this.saveTab(currentTabRef);
        },
        async goPreviousTab() {
            console.log('starting goPreviousTab');
            if (this.previousTabObj) {
                await this.goToTab(this.previousTabObj.ref);  // WITH all the error checking stuff
            }
        },
        async goNextTab() {
            console.log('starting goNextTab');
            if (this.nextTabObj) {
                await this.goToTab(this.nextTabObj.ref);  // WITH all the error checking stuff
            }
        },
        async goToTab(newTabRef, force, skipCheck) {
            // force (boolean) if true we still run checkTab, but will still change the tab
            // skipCheck (boolean) if true, we won't even run checkTab
            // DO ALL MANUAL TAB CHANGING THROUGH HERE TO CENTRALIZE LOGIC
            console.log('starting goToTab');
            const currentTabRef = this.currentTabObj.ref;
            this.previousVisitedTabRefs.add(currentTabRef);  // must add this before calling checkTab
            // always do the stuff above, even if skipCheck was true
            if (skipCheck) {
                console.log('skipCheck is set - no checkTab - will change tabs');
                this.changeTab(newTabRef);
                return;
            }
            const currentTabObj = await this.checkTab(currentTabRef);  // will have updated data in it from checkTab
            if (currentTabObj.isValid) {
                console.log('TAB VALID - OK to change');
                this.changeTab(newTabRef);
                return;
            } else {
                console.log('TAB INVALID - should show errors for this tab', currentTabObj.ref);
                if (force) {
                    console.log('force is true, so we will change tabs anyways');
                    this.changeTab(newTabRef);
                    return;
                }
            }
        },
        changeTab(newTabRef) {
            console.log('starting changeTab');
            // actually change to this tab, do not check anything - just go
            const tabIndex = this.tabs.findIndex(t => t.ref === newTabRef);
            if (tabIndex > -1) {
                this.currentTabIndex = tabIndex;  
            }
        },
        getTabObjectFromIndex(tabIndex) {
            // for getting your own tab objects from tab index (index is what's used by bootstrap vue tabs component)
            return this.tabs[tabIndex];  // will return undefined if index is out of range
        },
        async handleActivateTab(newIndex, prevIndex, event) {
            // called from @activate-tab event on b-tabs, event is cancellable using event.preventDefault()
            // maybe have multiple methods for changing tabs -- one that ignores any errors, one that does not, the one that does not ignore errors calls event.preventDefault() then manually goes to next tab if OK (using other method)
            // or just never cancel the event -- only update things in this.tabs from this, make separate methods for checking if you can change
            const newTab = this.getTabObjectFromIndex(newIndex);
            const prevTab = this.getTabObjectFromIndex(prevIndex);
            console.log('from handleActivateTab', newIndex, newTab, prevIndex, prevTab, event);
            /* NOT NEEDED IF YOU DO ALL MANUAL CHANGES THROUGH goToTab() method, where you already add currentTabRef to visited Set
            const prevTabRef = this.tabs[prevIndex] ? this.tabs[newIndex]['ref'] : '';
            if (prevTabRef) {
                this.previousVisitedTabRefs.add(prevTabRef);  // add this, so you can highlight errors if any
            } */
            //const newTabRef = this.tabs[newIndex] ? this.tabs[newIndex]['ref'] : '';
            // NO NEED TO CHECK THESE ALL THE TIME?  CHECK MANUALLY IN YOUR button handlers
            //const prevTabObj = await this.checkTab(prevTabRef);
            //console.log('prevTabObj', prevTabObj);
            //const newTabObj = await this.checkTab(newTabRef);
            //console.log('newTabObj', newTabObj);
            // FOR NOW, IF YOU NEED TO CALL ANYTHING WHEN A TAB IS ACTIVATED, JUST PUT HERE
            // later, you can make this a property on tab object ('on_tab_activated') or similar...
            const newTabRef = newTab.ref;
            if (newTabRef === 'tab3') {
                this.getPossibleActions();  // call this to update actions... otherwise changes to tasks will not be reflected
            }
        },
        handleTabsInput(tabIndex) {
            // triggered from @input on b-tabs, which also controls v-model
            // use this to track which tabs you visited, etc.
            console.log('from handleTabsInput', tabIndex);
            //const tabRef = this.tabs[tabIndex] ? this.tabs[tabIndex]['ref'] : '';
        },
        async checkAllTabs() {
            // call checkTab on all the tabs -- do this when you have all data loaded
            return await this.tabs.map(t => {
                return this.checkTab(t.ref)
            });
        },
        async checkCurrentTab() {
            return await this.checkTab(this.currentTabObj.ref);
        },
        // add something about showing tab status vs. checking it -- only show if it was visited?
        async checkTab(tabRef) {
            // this gets called frequently, anytime we change tabs, or try to change tabs
            // this updates the tab object in place, then returns the new full tab object so you can get things from it without needing to find tab in this.tabs array again
            //      isValid, hasUnsaved, disabled, errors -- will get set in $data.tabs
            // you can do async things if needed
            //const tabRef = tabObj.ref;
            const tabObj = this.tabs.find(t => t.ref === tabRef);  // in case you only pass in tabRef
            if (!tabObj) {
                console.log('NO tabObj from checkTab!');
                return tabObj;  // will be undefined in this case -- but we are supposed to return the updated tabObj anyways, so it makes sense to return this
            }
            const newTabData = {  // set defaults, then modify as you go - set isValid and wasVisited at the end
                errors: [],
                hasUnsaved: false,
                disabled: false,
                isValid: true,
                wasVisited: false,
                showErrors: false,
            };  
            if (tabRef === 'tab1') {
                if (!this.instanceData.title) {
                    newTabData.errors.push('Title is required');
                }
                if (!this.instanceData.owner) {
                    newTabData.errors.push('Owner is required');
                }
                if (!this.instanceData.vendor && !this.isInternalOnly) {
                    newTabData.errors.push('Vendor is required');
                }
                newTabData.hasUnsaved = this.checkHasUnsavedInstanceData();
                // not setting disabled for now
            }
            if (tabRef === 'tab2') {
                const stepNeedsAttention = this.stepsWithTasks.some(step => step.isError);  // we already check the list of tasks and set it on step.isError, so just use that
                if (stepNeedsAttention) {
                    newTabData.errors.push('Must finish setting up all tasks');
                }
                // not changing hasUnsaved or disabled for now - each task will be disabled on its own if needed
            }
            if (tabRef === 'tab3') {
                // nothing to do... keep defaults
            }
            // GENERIC THINGS SHARED BY ALL - DRY
            newTabData.wasVisited = this.previousVisitedTabRefs.has(tabRef);  // make sure you add things to Set before you call this
            newTabData.isValid = newTabData.errors.length < 1;
            newTabData.showErrors = !this.hideAllErrors && (newTabData.wasVisited && !newTabData.isValid);  // if not hiding all errors AND was visited but has errors

            // NOW UPDATE the tabObj -- use Vue.$set?  just mutate tabObj?
            const tabIndex = this.tabs.findIndex(t => t.ref === tabRef);  // should be there, since we did .find() up at the top
            const updatedTabObj = Object.assign({}, tabObj, newTabData);  // this makes a new object...
            this.$set(this.tabs, tabIndex, updatedTabObj);  // will replace the entire tab object in the tabs array, in a reactive way
            console.log('tabObj from checkTab', updatedTabObj);
            return updatedTabObj;  // return this, so you can grab things from it without having to find it in this.tabs array again
        },
        // end of tab controls / methods
        ////////////////////////////////////////
        enterEditMode() {
            this.instanceEditMode = true;
        },
        cancelEditMode() {
            // have to restore old values
            this.instanceData = Object.assign({}, this.lastSavedInstanceData);
            this.instanceEditMode = false;
        },
        checkHasUnsavedInstanceData() {
            // making this a method instead of computed so we can control exactly when its called, and because the stuff changing is nested
            this.hasUnsavedInstanceData = !isEqual(this.lastSavedInstanceData, this.instanceData);
            return this.hasUnsavedInstanceData;
        },
        formatLocalInstanceData(runObj) {  // pass in the instance of your model class from API to set local data
            // just return data in the structure needed for updates of it -- ie: with PKs instead of full objects
            // changed this to go through each item individually... so you can use it for partial updates too
            const instData = {
                title: runObj.title,  // at some point you can probably remove title from instance data... its being update on its own
                owner: runObj.owner_id,
                vendor: runObj.vendor_id,
                vendor_members: runObj.vendor_members.map(tu => tu.id),
                internal_members: runObj.internal_members.map(tu => tu.id),
            };
            // NEW - we'll add owner.id to internal_members so it visually makes sense -- then remove it before calling API if we update it
            // do it in computed instead, so it live updates if you change owner
            /* if (instData.owner && instData.internal_members) {
                instData.internal_members.push(instData.owner);
            } */
            return instData;
        },  
        handleApiResponse(apiData) {
            // DRY for handling response from getDetails or updateInstanceData
            // NOT USED FOR PARTIAL UPDATES (so we don't lose any unsaved changes in other fields) -- separate one for that 
            // updated Feb 15 2022 - to handle tasks not being nested in workflow_run
            // depending on endpoint we might have tasks nested in apiData, or might have 'workflow_run' and 'tasks' separate
            const workflowRunData = apiData.workflow_run ? apiData.workflow_run : apiData;
            const tasksList = apiData.tasks;  // for now, no matter the format, tasks will always be one level deep, under 'tasks' key
            this.metadata = Object.assign({}, workflowRunData.metadata);
            this.workflow_spec = Object.assign({}, workflowRunData.workflow_spec);
            // set selected members
            this.selectedInternalMemberObjects = cloneDeep(workflowRunData.internal_members);
            // add owner - might not be same shape as internal_members?  but should have all the main properties
            //if (workflowRunData.owner) {
            //    this.selectedInternalMemberObjects.push(workflowRunData.owner);
            //}  // we merge it in a different way, in computed
            this.selectedInternalMemberIds = this.selectedInternalMemberObjects.map(tuObj => tuObj.id);
            this.selectedVendorMemberObjects = cloneDeep(workflowRunData.vendor_members);
            this.selectedVendorMemberIds = this.selectedVendorMemberObjects.map(tuObj => tuObj.id);
            this.title = workflowRunData.title;
            this.selectedVendorObj = workflowRunData.vendor;
            this.selectedOwnerObj = workflowRunData.owner;
            this.selectedOwnerId = this.selectedOwnerObj ? this.selectedOwnerObj.id : '';
            //this.setPossibleTransitions(apiData.possible_transitions);
            this.setPossibleActions(workflowRunData.possible_actions);
            const tasks = cloneDeep(tasksList);
            // OLD this.tasksById = Object.fromEntries(tasks.map(t => [t.id, t]));
            this.tasksById = Object.fromEntries(tasks.map(t => [t.id, new DwfTask(t)]));
            const runData = new WorkflowRun(workflowRunData);
            this.runData = Object.assign({}, runData);
            const instData = this.formatLocalInstanceData(this.runData);
            //this.lastSavedInstanceData = cloneDeep(instData);
            this.lastSavedInstanceData = cloneDeep(instData); // clone it to decouple it  
            this.instanceData = Object.assign({}, instData);
            if (runData.vendor?.id) {  // pull out the set vendor obj and set it on top level for use by v-model of selector
                const { id, name } = runData.vendor;
                this.selectedVendorObj = Object.assign({}, { id, name });
            }
            if (!this.instanceData.vendor) {  // do you have a vendor selected?  if not, go straight to edit mode
                this.enterEditMode();
                //this.instanceEditMode = true;
            } else {  // if you DO have a vendor, turn off edit mode
                this.instanceEditMode = false;
            }
            this.hideAllErrors = false;  // starts true when created, then we set false after everything is loaded, but just before we call checkAllTabs -- do this to hide errors before things have had a chance to load
            this.checkAllTabs();
            // no return... just set things
        },
        async getDetails() {
            this.loading = true;
            try {
                const { data } = await suppliosApi.workflowRun.get_detail(this.runId);  // NEW - data is { workflow_run, tasks }
                this.handleApiResponse(data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        async getUpdateOptions() {
            this.loadingUpdateOptions = true;
            try {
                const { data } = await suppliosApi.workflowRun.get_options_for_update(this.runId);
                this.updateOptions = Object.assign({}, data);
                this.ownerOptions = [ ...this.updateOptions.owner_options ];
                this.internalMemberOptions = [ ...this.updateOptions.internal_member_options ];
                //this.updateFinalMemberOptions();
                this.vendorOptions = [ ...this.updateOptions.vendor_options ];
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingUpdateOptions = false;
        },
        async getVendorMemberOptions(vendorId) {  // passing in vendorId is optional - but needed if no selection yet
            this.loadingVendorMemberOptions = true;
            try {
                const axiosConfig = vendorId ? {params: {vendor_id: vendorId}} : null;
                const { data } = await suppliosApi.workflowRun.get_vendor_member_options(this.runId, axiosConfig);
                this.vendorMemberOptions = [...data];
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingVendorMemberOptions = false;
        },
        async updateTitle() {  // call on blur event or similar
            // NEW FEB 16 2022 - simpler version, not using partialUpdateInstanceData
            const isChanged = this.instanceData.title !== this.lastSavedInstanceData.title;
            console.log('from updateTitle - isChanged', isChanged);
            if (isChanged) {
                console.log('isChanged is TRUE - making api call');
                const updateData = {title: this.instanceData.title};  // what we'll send
                this.loadingTitle = true;
                try {
                    const { data } = await suppliosApi.workflowRun.partial_update_instance(this.runId, updateData);
                    // this may return the full object for WorkflowRun -- but you should not overwrite the local copy of it, in case you have local unsaved changes -- they'll get blasted away
                    // it does not return all the things that are same as get_detail -- so DO NOT call method to see all things from API data - just get the title
                    const updatedTitle = data.title;  // in case server side did anything with it
                    this.$set(this.runData, 'title', updatedTitle);
                    this.$set(this.instanceData, 'title', updatedTitle);
                    this.$set(this.lastSavedInstanceData, 'title', updatedTitle);
                    this.showTitleUpdated();  // just trigger it and move on
                    this.checkAllTabs();  // refresh our checks
                } catch (err) {
                    this.apiErrorHandler(err);
                }
                this.loadingTitle = false;
            }
        },
        showTitleUpdated() {
            this.titleUpdatedShowSuccess = true;
            setTimeout(() => {
                this.titleUpdatedShowSuccess = false;
            }, 2000);
        },
        async updateInstanceData() {
            this.savingUpdate = true;
            try {
                //const updateData = this.formatInstanceDataForApi();
                const updateData = cloneDeep(this.instanceData);  // Updates Feb 16 2022 - the owner ID should already be removed from internal_members, so we should be ready to go as is
                const { data } = await suppliosApi.workflowRun.update_instance(this.runId, updateData);
                // response here is same as from original getDetails
                this.handleApiResponse(data);
            } catch (err) {
                // error response from DRF will have { field: ['list of error messagges']}
                this.apiErrorHandler(err);
            }
            this.savingUpdate = false;
        },
        handleInternalMemberChecked(checkedArray) {
            // value will be the value of internal members checkbox group -- an array
            //console.log('from handleInternalMemberChecked - payload:', checkedArray);
            // remove owner id if there is one - then we have computed property that adds it back... for :checked
            const ownerId = this.instanceData.owner;
            this.instanceData.internal_members = ownerId ? checkedArray.filter(memId => memId !== ownerId) : checkedArray;
            //this.selectedInternalMemberIds = checkedArray;
        },
        handleVendorInput(vendor) {
            // will be the newly selected vendorId -- must check if its different
            let vendorId = '';
            if (!vendor) {  // have to handle vendor being null if we 'clear' the vendor 
                vendorId = '';
            } else {
                vendorId = typeof vendor === 'string' ? vendor : vendor.id;
            }
            // NEW - with Dropdown component - will be an object! - ternary above should handle both - but can remove string version later
            const lastSavedVendorId = this.lastSavedInstanceData.vendor;
            const isChanged = lastSavedVendorId !== vendorId;
            if (isChanged) {
                this.$set(this.instanceData, 'vendor_members', []);
                //this.instanceData.vendor_members = [];  // clear existing selections!
                this.getVendorMemberOptions(vendorId);
            }
        },
        handleNewVendorContact(newContact) {
            // from 'create-complete' event on contact-create-update-modal - payload will be new contact TU object
            console.log('create-complete done', newContact);
            // ADD NEW TU TO VENDOR MEMBER OPTIONS
            this.vendorMemberOptions.push(newContact);  // is this reactive???
            //this.updateFinalMemberOptions();  // refresh this
            // THEN SELECT THE NEW TU -- will still have to save the new instance data
            this.instanceData.vendor_members.push(newContact.id);
        },
        async loadMetadata() {  // not needed... likely no longer calling it in mounted()
            try {
                const { data } = await suppliosApi.workflowRun.get_metadata_with_schema(this.runId);
                console.log('get_metadata_with_schema', data);
                this.metadataSchema = data.metadata_schema;
                this.metadata = data.metadata;
            } catch (err) {
                this.apiErrorHandler(err);
            }
        },
        handleTaskUpdated(newTaskInst) {
            // child component will pass new DwfTask instance in @updated event payload - update in our tasksById 'store'
            console.log('New task instance from child component', newTaskInst);
            this.$set(this.tasksById, newTaskInst.id, newTaskInst); 
            this.checkCurrentTab(); 
        },
        setPossibleActions(possibleActions) {
            // will be a list of strings
            if (!possibleActions) return;
            this.possibleActions = [...possibleActions];  // just a list of strings, so this is good
        },
        async getPossibleActions() {
            // call this when you update things to refresh possible actions (if you didn't reload everything)
            this.loadingPossibleActions = true;
            try {
                const { data } = await suppliosApi.workflowRun.get_possible_actions(this.runId);
                this.setPossibleActions(data);   // data is just an array of strings
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingPossibleActions = false;
        },
        async callAction(actionName) {  // actionName is a string like 'start_workflow'
            this.loadingAction = true;
            try {
                const postData = {action: actionName};  // anything else??? send along any other kwargs needed for transition (which is inside action)
                const resp = await suppliosApi.workflowRun.call_action(this.runId, postData);
                // the entire Run instance is returned 
                this.handleApiResponse(resp.data); 
                // for now we'll hard-code in a redirect to details page
                // get it from the new local data you set to make sure you deal with consistent format/structure of response
                //const internalDetailsUrl = resp.data?.urls?.internal_detail;
                const internalDetailsUrl = this.runData.urls.internal_detail;
                if (internalDetailsUrl) {
                    window.location.assign(internalDetailsUrl);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingAction = false;
        },
    }
}
</script>

<style>

</style>